import {FormModel} from '@xh/hoist/cmp/form';
import {HoistModel, LoadSpec, managed, SelectOption, TaskObserver, XH} from '@xh/hoist/core';
import {action, observable} from '@xh/hoist/mobx';
import {AccountStructureItemType, HoldingsTypeEnum, ManagementTypeEnum, Portfolio, TaxTreatmentEnum} from '../../../data/DataTypes';
import {required} from '@xh/hoist/data';
import {INodeData} from '../../../cmp/cytoGraph/CytoGraphTypes';

export interface DetailsModelSpec {
    formModel: FormModel;
    isDirty: boolean;
    isValid: boolean;

    load(nodeData: INodeData);
    refresh();
    saveAsync(): Promise<any>;
}

export class ItemDetailsModel extends HoistModel {
    apiTask = TaskObserver.trackLast();
    @observable accessor itemType: AccountStructureItemType;
    @observable @managed accessor detailsModel: DetailsModelSpec;

    @action
    clear() {
        this.itemType = null;
        XH.safeDestroy(this.detailsModel);
    }

    revert() {
        this.detailsModel.refresh();
    }

    async saveAsync() {
        await this.detailsModel.saveAsync().linkTo(this.apiTask);
        await XH.portfolioService.reloadAsync();
        await this.detailsModel.refresh();
    }

    get isDirty(): boolean {
        return this.detailsModel?.isDirty;
    }

    get isValid(): boolean {
        return this.detailsModel?.isValid;
    }

    @action
    async openAsync(node: INodeData) {
        this.itemType = node.customData.type;
        XH.safeDestroy(this.detailsModel);

        if (this.itemType == AccountStructureItemType.BROKERAGE) {
            this.detailsModel = new BrokerageDetailsModel();
        } else if (this.itemType == AccountStructureItemType.PORTFOLIO) {
            this.detailsModel = new PortfolioDetailsModel();
        } else if (this.itemType == AccountStructureItemType.CUSTOM) {
            this.detailsModel = new CustomDetailsModel();
        }

        this.detailsModel.load(node);
    }
}

export class BrokerageDetailsModel extends HoistModel implements DetailsModelSpec {
    @managed formModel = this.createFormModel();
    private nodeData: INodeData;

    load(nodeData: INodeData) {
        this.nodeData = nodeData;
        this.loadAsync();
    }

    refresh() {
        this.loadAsync();
    }

    async saveAsync() {
        const {accountStructure} = XH.portfolioService,
            element = accountStructure.items.find(i => i.data.id == this.nodeData.id),
            newName = this.formModel.getData().name;

        element.data.customData.name = newName;

        await XH.portfolioService.saveAccountStructureAsync(accountStructure);
        await XH.portfolioService.refreshPortfoliosAsync();
        await XH.livePositionsService.loadAsync();
    }

    get isDirty(): boolean {
        return this.formModel.isDirty;
    }

    get isValid(): boolean {
        return this.formModel.isValid;
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        this.formModel.init({name: this.nodeData.customData.name});
    }

    private createFormModel(): FormModel {
        return new FormModel({
            fields: [{name: 'name', rules: [required]}]
        });
    }
}

export class PortfolioDetailsModel extends HoistModel implements DetailsModelSpec {
    @managed formModel = this.createFormModel();
    private portfolioId: number;

    load(nodeData: INodeData) {
        this.portfolioId = nodeData.customData.portfolioId;
        this.loadAsync();
    }

    refresh() {
        this.loadAsync();
    }

    async saveAsync() {
        const portfolio = this.formModel.getData() as Portfolio;
        await XH.portfolioService.savePortfolioAsync(portfolio);
    }

    get isDirty(): boolean {
        return this.formModel.isDirty;
    }

    get isValid(): boolean {
        return this.formModel.isValid;
    }

    get managementOptions(): SelectOption[] {
        return [
            {value: ManagementTypeEnum.Managed, label: 'Managed'},
            {value: ManagementTypeEnum.Direct, label: 'Directed'}
        ];
    }

    get holdingsOptions(): SelectOption[] {
        return [
            {value: HoldingsTypeEnum.Investment, label: 'Investment'},
            {value: HoldingsTypeEnum.Cash, label: 'Cash'},
            {value: HoldingsTypeEnum.Spec, label: 'Spec'},
            {value: HoldingsTypeEnum.Hedge, label: 'Hedge'},
            {value: HoldingsTypeEnum.Loan, label: 'Loan'}
        ];
    }

    get taxableOptions(): SelectOption[] {
        return [
            {value: TaxTreatmentEnum.Taxable, label: 'Taxable'},
            {value: TaxTreatmentEnum.TaxDeferred, label: 'Tax-deferred'},
            {value: TaxTreatmentEnum.TaxFree, label: 'Tax-free'}
        ];
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const data = XH.portfolioService.portfoliosById.get(this.portfolioId);
        this.formModel.init(data);
    }

    private createFormModel(): FormModel {
        return new FormModel({
            fields: [
                {name: 'id'},
                {name: 'userId'},
                {name: 'portfolioName', rules: [required]},
                {name: 'portfolioId'},
                {name: 'brokerage', disabled: true},
                {name: 'isActive'},
                {name: 'managementType', rules: [required]},
                {name: 'holdingsType', rules: [required]},
                {name: 'taxableType', rules: [required]},
                {name: 'updated'},
                {name: 'updatedBy'},
                {name: 'timestamp'}
            ]
        });
    }
}

export class CustomDetailsModel extends HoistModel implements DetailsModelSpec {
    @managed formModel = this.createFormModel();
    private nodeData: INodeData;

    load(nodeData: INodeData) {
        this.nodeData = nodeData;
        this.loadAsync();
    }

    refresh() {
        this.loadAsync();
    }

    async saveAsync() {
        const {accountStructure} = XH.portfolioService,
            element = accountStructure.items.find(i => i.data.id == this.nodeData.id),
            newName = this.formModel.getData().name;

        element.data.customData.name = newName;

        await XH.portfolioService.saveAccountStructureAsync(accountStructure);
        //await XH.portfolioService.refreshPortfoliosAsync();
        //await XH.livePositionsService.loadAsync();
    }

    get isDirty(): boolean {
        return this.formModel.isDirty;
    }

    get isValid(): boolean {
        return this.formModel.isValid;
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        this.formModel.init({name: this.nodeData.customData.name});
    }

    private createFormModel(): FormModel {
        return new FormModel({
            fields: [{name: 'name', rules: [required]}]
        });
    }
}
